import React from 'react';
import {
  PILL_KIND,
  RPT_TOKENS,
  TIME_RANGES,
} from '../../reports/config';
import type { PillKind, ReportsTimeRange } from '../../reports/types';
import { Icon } from '../Icon/Icon';

export interface ReportPageHeaderProps {
  eyebrow: string;
  title: string;
  italic?: string;
  description: string;
  metric?: number | string;
  metricLabel?: string;
  timeRange: ReportsTimeRange;
  onTimeRangeChange: (value: ReportsTimeRange) => void;
}

export function ReportPageHeader({
  eyebrow,
  title,
  italic,
  description,
  metric,
  metricLabel,
  timeRange,
  onTimeRangeChange,
}: ReportPageHeaderProps) {
  return (
    <header className="mb-7 flex items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        <div className="mb-2.5 flex items-center gap-2">
          <Icon kind="sparkle" size={13} color={RPT_TOKENS.green} stroke={2} />
          <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: RPT_TOKENS.green }}>
            {eyebrow}
          </span>
        </div>
        <h1 className="m-0 text-[46px] font-semibold leading-none tracking-[-0.035em] text-[var(--ink-0)]">
          {title}
          {italic && <> <span className="rpt-count-gradient">{italic}</span></>}
        </h1>
        <p className="mt-3 max-w-[700px] text-[14px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
          {description}
        </p>
      </div>
      <div className="flex flex-none flex-col items-end gap-2.5">
        <label className="relative inline-flex">
          <select
            value={timeRange}
            onChange={(e) => onTimeRangeChange(e.target.value as ReportsTimeRange)}
            className="h-9 cursor-pointer appearance-none rounded-[9px] border border-[rgba(28,24,18,0.15)] bg-[var(--cream-1)] py-0 pl-3.5 pr-9 text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)] shadow-[var(--sh-1)] outline-none"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <Icon
            kind="down"
            size={14}
            color={RPT_TOKENS.ink4}
            stroke={2}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
          />
        </label>
        {metric != null && metricLabel && (
          <div className="rpt-kpi-card min-w-[220px] rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-3.5 text-right shadow-[var(--sh-1)]">
            <div className="rpt-tab-num mb-1 text-[32px] font-semibold leading-none tracking-[-0.03em] text-[var(--ink-0)]">
              {metric}
            </div>
            <div className="text-[11.5px] tracking-[-0.005em] text-[var(--ink-2)]">{metricLabel}</div>
          </div>
        )}
      </div>
    </header>
  );
}

export function InsightBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-5 flex items-start gap-2.5 rounded-[11px] px-[18px] py-3.5"
      style={{ background: RPT_TOKENS.brassTint, border: `1px solid ${RPT_TOKENS.brassSoft}` }}
    >
      <Icon kind="sparkle" size={12} color={RPT_TOKENS.brassDeep} stroke={2} className="mt-0.5 shrink-0" />
      <div className="text-[13px] leading-[1.6] tracking-[-0.005em] text-[var(--ink-1)]">{children}</div>
    </div>
  );
}

export function TrendCell({ value }: { value: number | null | undefined }) {
  if (value === 0 || value == null) {
    return (
      <span className="rpt-mono inline-flex items-center gap-1 text-[12px] font-medium text-[var(--ink-2)] opacity-70">
        — 0%
      </span>
    );
  }
  const positive = value > 0;
  return (
    <span
      className="rpt-mono inline-flex items-center gap-1 text-[12px] font-semibold"
      style={{ color: positive ? RPT_TOKENS.greenDeep : RPT_TOKENS.negative }}
    >
      {positive ? '▲' : '▼'}
      {positive ? '+' : ''}
      {value}%
    </span>
  );
}

export function KindPill({ kind, label }: { kind: PillKind; label: string }) {
  const cfg = PILL_KIND[kind] ?? PILL_KIND.neutral;
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-[3px] text-[11.5px] font-semibold tracking-[-0.005em]"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {label}
    </span>
  );
}

export function ReportTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
      <table className="w-full border-collapse">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  width,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  width?: number;
}) {
  return (
    <th
      className="rpt-eyebrow whitespace-nowrap border-b border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-3 text-[10px]"
      style={{ textAlign: align, width }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  color,
  weight,
  mono,
  maxWidth,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  color?: string;
  weight?: number;
  mono?: boolean;
  maxWidth?: number;
}) {
  return (
    <td
      className={`border-b border-[var(--cream-2)] px-4 py-3.5 align-middle text-[13px] tracking-[-0.005em] ${mono ? 'rpt-mono' : ''}`}
      style={{ textAlign: align, color: color ?? 'var(--ink-1)', fontWeight: weight ?? 400, maxWidth }}
    >
      {children}
    </td>
  );
}

export function SearchBar({ placeholder }: { placeholder: string }) {
  return (
    <div className="relative mb-5">
      <Icon
        kind="search"
        size={14}
        color={RPT_TOKENS.ink4}
        stroke={2}
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
      />
      <input
        placeholder={placeholder}
        className="h-10 w-full rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-1)] py-0 pl-10 pr-3.5 text-[13px] tracking-[-0.005em] text-[var(--ink-0)] shadow-[var(--sh-1)] outline-none"
      />
    </div>
  );
}

export function SectionHeader({
  num,
  title,
  subtitle,
  accent = 'green',
}: {
  num: string;
  title: React.ReactNode;
  subtitle: string;
  accent?: 'green' | 'brass';
}) {
  const accentColor = accent === 'brass' ? RPT_TOKENS.brassDeep : RPT_TOKENS.green;
  return (
    <header className="mb-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="rpt-mono rounded-[5px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2 py-0.5 text-[11px] font-semibold text-[var(--ink-2)] shadow-[var(--sh-1)]">
          {num}
        </span>
        <span className="rounded-sm" style={{ width: 3, height: 22, background: accentColor }} />
        <div>
          <div className="text-[19px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">{title}</div>
          <div className="mt-0.5 text-[12.5px] tracking-[-0.005em] text-[var(--ink-2)]">{subtitle}</div>
        </div>
      </div>
    </header>
  );
}

export function AddToBrainButton() {
  return (
    <button
      type="button"
      className="inline-flex h-7 cursor-pointer items-center gap-[5px] rounded-[7px] border px-[11px] text-[11.5px] font-semibold tracking-[-0.005em]"
      style={{
        background: RPT_TOKENS.greenWash,
        color: RPT_TOKENS.greenDeep,
        borderColor: RPT_TOKENS.greenSoft,
      }}
    >
      <Icon kind="sparkle" size={11} stroke={2} />
      Add to Brain
    </button>
  );
}
